import { v4 as uuidV4 } from 'uuid';
import cloneDeep from 'lodash.clonedeep';
import { autoCalculateHighValue } from 'hypo-fee-schedule-summary-list/src/utils';
import { getFundFamiliesDataFromApi, getSMASecuritiesBaseInfoFromApi } from './data-source';
import {
  FEE_SCHEDULE_TYPES,
  FRONT_LOAD_FEE_TYPE_OPTIONS,
  FEE_CONFIG_TYPE,
  USER_LEVEL,
  SOURCE,
  FEE_TYPES,
  PLAN_FEE_TYPES,
  FRONT_LOAD_TYPES,
  FRONT_LOAD_AMOUNT_TYPES,
} from './utils/constants';
import {
  omit,
  equal,
  frontLoadTypeDataMapping,
  isPercentageInRange,
  isMaxFee,
} from './utils/utils';
import {
  isPrivateModel,
  isSeparateAccount,
  setAdvisoryFeeDataForSecurity,
  getAdvisoryFeeSchedule,
} from './components/shared/shared-advisory-fee-helpers';

const { TIERED_FEE_BY_BREAKPOINT, TOTAL_FEE } = FEE_SCHEDULE_TYPES;
const { EVERYTHING_OFF, ONLY_FLAT_PERCENT, ONLY_FEE_CHOICE, EVERYTHING_ON } = FEE_CONFIG_TYPE;
const { MAX_OR_FEE_SCHEDULE, FLAT, VARIES } = FEE_TYPES;
const { ESTIMATED_PERCENT, BENCHMARK_PERCENT } = PLAN_FEE_TYPES;
const { STANDARD, CUSTOM, LOI, VAH } = FRONT_LOAD_TYPES;
const { PERCENT, AMOUNT } = FRONT_LOAD_AMOUNT_TYPES;

export const getProgramFeeSchedules = async privateModelSecurities => {
  const programFeeScheduleMap = new Map();
  privateModelSecurities.forEach(security => {
    const { programId } = security.fee;
    if (!programFeeScheduleMap.has(programId)) {
      programFeeScheduleMap.set(programId, {
        ...security.fee,
        annualFeeType: 0,
        feeScheduleId: uuidV4(),
      });
    } else {
      /** Use the most time schedule tiers to simplify the total fee schedule calculation */
      const currentFeeSchedule = programFeeScheduleMap.get(programId);
      const { feeScheduleDetails: currentFeeScheduleDetails = [] } = currentFeeSchedule ?? {};
      const { feeScheduleDetails = [] } = security.fee ?? {};
      if (currentFeeScheduleDetails.length < feeScheduleDetails.length) {
        programFeeScheduleMap.set(programId, {
          ...currentFeeSchedule,
          feeScheduleDetails,
        });
      }
    }
  });
  return Array.from(programFeeScheduleMap.values());
};

const updateSecurityWithFeeData = (security, investmentFeeSchedule) => {
  const feeChargeFrequencies = {
    monthly: 1,
    quarterly: 3,
    semi: 6,
    annually: 12,
  };
  const { allowMaxDiscount, maxFee, feeFrequency, feeScheduleId } = investmentFeeSchedule ?? {};
  let { annualMaxFee, discount } = security.fees ?? {};

  if (isMaxFee(investmentFeeSchedule) && allowMaxDiscount) {
    annualMaxFee = annualMaxFee || maxFee;
    discount = discount || 0;
  }
  return {
    ...security,
    fees: {
      ...security.fees,
      annualFeeType: 0,
      feeScheduleId,
      annualMaxFee,
      discount,
      annualFeeChargeFrequency: feeChargeFrequencies[feeFrequency],
    },
    advisoryFeeSchedule: investmentFeeSchedule,
  };
};

export const setProgramFeeForAdvisoryFeeTableData = (
  advisoryFeeTableData,
  programFeeSchedules,
  privateModelSecurities
) => {
  return advisoryFeeTableData.map(security => {
    const investmentFeeSchedule = programFeeSchedules.find(
      schedule => schedule.programId === security.programId
    );

    const updatedSecurity = updateSecurityWithFeeData(security, investmentFeeSchedule);

    if (isPrivateModel(security.securityType) && investmentFeeSchedule?.separateAccountManagerFee) {
      const privateModelSecurity = privateModelSecurities.find(
        pmSec => pmSec.securityId === security.securityId
      );

      return {
        ...updatedSecurity,
        managerFee: privateModelSecurity?.managerFee,
        historicManagerFee: privateModelSecurity?.historicManagerFee,
      };
    }
    return updatedSecurity;
  });
};

export const addFunFamilyDataToSecurityTableData = (data, securityTableData) => {
  if (!data || data.length === 0) {
    return securityTableData;
  }
  return securityTableData.map(security => {
    const secWithFundFamilyInfo = data.find(i => i.secId === security.securityId);
    if (secWithFundFamilyInfo) {
      const { fundFamilyId, fundFamilyName } = secWithFundFamilyInfo;
      return {
        ...security,
        fundFamilyId,
        fundFamilyName,
      };
    }
    return security;
  });
};

export const getFundFamiliesData = async (securities, tokenSource, env) => {
  const noFundFamiliesSecurities = securities.filter(
    security => security.fundFamilyId === undefined && security.fundFamilyName === undefined
  );
  let data = [];
  if (noFundFamiliesSecurities.length > 0) {
    const secIds = noFundFamiliesSecurities.map(sec => {
      return sec.securityId;
    });
    data = await getFundFamiliesDataFromApi(tokenSource, env, secIds);
  }
  return data;
};

export const adjustFeeScheduleDetails = feeSchedule => {
  const { feeScheduleType, feeScheduleDetails } = feeSchedule;
  // Tiered by breakpoint/Total fee
  const isBreakPointOrTotalFee =
    (feeScheduleType === TIERED_FEE_BY_BREAKPOINT || feeScheduleType === TOTAL_FEE) &&
    feeScheduleDetails &&
    feeScheduleDetails.length > 0;

  if (!isBreakPointOrTotalFee) {
    return feeSchedule;
  }

  let fractionDigits = 0;
  let needMinus = false;
  if (feeScheduleType === TOTAL_FEE) {
    // Total Fee
    fractionDigits = 2;
    needMinus = true;
  }
  const newFeeScheduleDetails = autoCalculateHighValue(
    feeScheduleDetails,
    fractionDigits,
    needMinus
  );
  return {
    ...feeSchedule,
    feeScheduleDetails: newFeeScheduleDetails,
  };
};

const getFundfamilies = securityTableData => {
  return securityTableData.reduce((prev, security) => {
    const { fundFamilyId, fundFamilyName, frontLoadFeeType } = security;
    const isExist = (fundFamilies, id) =>
      fundFamilies.find(i => i.fundFamilyId === id) !== undefined;
    if (
      fundFamilyId === undefined ||
      isExist(prev, fundFamilyId) ||
      frontLoadFeeType !== FRONT_LOAD_FEE_TYPE_OPTIONS.STANDARD
    ) {
      return prev;
    }
    prev.push({
      fundFamilyId,
      fundFamilyName,
    });
    return prev;
  }, []);
};

export const getRoaFundFamilies = securityTableData => {
  const fundFamilies = getFundfamilies(securityTableData);
  return fundFamilies.map(item => {
    const secWithSameRoa = securityTableData.find(
      security =>
        security.frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.STANDARD &&
        security.fundFamilyId === item.fundFamilyId &&
        security.roaAmount &&
        security.roaAmount !== 0
    );
    const { roaAmount = 0 } = secWithSameRoa || {};
    return {
      ...item,
      fundFamilyAmount: roaAmount,
    };
  });
};

export const getLoiFundFamilies = securityTableData => {
  const fundFamilies = getFundfamilies(securityTableData);
  return fundFamilies.map(item => {
    const secWithSameLoi = securityTableData.find(
      security =>
        security.frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.STANDARD &&
        security.fundFamilyId === item.fundFamilyId &&
        security.loiAmount &&
        security.loiAmount !== 0
    );
    const { loiAmount = 0 } = secWithSameLoi || {};
    return {
      ...item,
      fundFamilyAmount: loiAmount,
    };
  });
};

export const getFeesIncludedText = (
  includeAdvisoryFee,
  includeTransactionFee,
  showAdvisoryFee,
  showTransactionFee,
  showPlanFee,
  includePlanFee,
  thisOhj
) => {
  if (!showAdvisoryFee && !showTransactionFee) {
    return '';
  }
  const showButNotIncludeAdvisoryFee = showAdvisoryFee && !includeAdvisoryFee;
  const showButNotIncludeTransactionFee = showTransactionFee && !includeTransactionFee;
  if (showPlanFee) {
    if (!includeTransactionFee && !includeAdvisoryFee && !includePlanFee) {
      return thisOhj.translate('feesNotIncluded');
    }
    if (showButNotIncludeAdvisoryFee && showButNotIncludeTransactionFee) {
      return thisOhj.translate('advisoryFeeAndTransactionFeeNotIncluded');
    }
    if (showButNotIncludeAdvisoryFee && !includePlanFee) {
      return thisOhj.translate('advisoryFeeAndPlanFeeNotIncluded');
    }
    if (showButNotIncludeTransactionFee && !includePlanFee) {
      return thisOhj.translate('planFeeAndTransactionFeeNotIncluded');
    }
  } else {
    if (!includeTransactionFee && !includeAdvisoryFee) {
      return thisOhj.translate('feesNotIncluded');
    }
    if (showButNotIncludeAdvisoryFee) {
      return thisOhj.translate('advisoryFeeNotIncluded');
    }
    if (showButNotIncludeTransactionFee) {
      return thisOhj.translate('transactionFeeNotIncluded');
    }
  }
  return '';
};

const isEqualAnnualFrequency = advisoryFeeTableData => {
  return advisoryFeeTableData.every(
    security =>
      security?.fees?.annualFeeChargeFrequency ===
      advisoryFeeTableData[0]?.fees?.annualFeeChargeFrequency
  );
};

const isEqualFeeScheduleId = advisoryFeeTableData => {
  return advisoryFeeTableData.every(security => {
    return security?.fees?.feeScheduleId === advisoryFeeTableData[0]?.fees?.feeScheduleId;
  });
};

const isEqualAnnualFeeType = advisoryFeeTableData => {
  return advisoryFeeTableData.every(
    security => security?.fees?.annualFeeType === advisoryFeeTableData[0]?.fees?.annualFeeType
  );
};

const isEqualFlatAnnualFeeAmount = advisoryFeeTableData => {
  return advisoryFeeTableData.every(
    security => security?.fees?.annualFeeAmount === advisoryFeeTableData[0]?.fees?.annualFeeAmount
  );
};

const isEqualAnnualFeeData = advisoryFeeTableData => {
  const sample = advisoryFeeTableData[0]?.fees;
  const isFlatFeeType = sample?.annualFeeType === FLAT;
  return (
    isEqualAnnualFeeType(advisoryFeeTableData) &&
    (isFlatFeeType
      ? isEqualFlatAnnualFeeAmount(advisoryFeeTableData)
      : isEqualFeeScheduleId(advisoryFeeTableData))
  );
};

const isEqualAnnualMaxFeeAmount = advisoryFeeTableData => {
  return advisoryFeeTableData.every(security => {
    return (
      security?.fees?.feeScheduleId === advisoryFeeTableData[0]?.fees?.feeScheduleId &&
      security?.fees?.annualMaxFee === advisoryFeeTableData[0]?.fees?.annualMaxFee
    );
  });
};

const isEqualDiscount = advisoryFeeTableData => {
  return advisoryFeeTableData.every(security => {
    return (
      security?.fees?.feeScheduleId === advisoryFeeTableData[0]?.fees?.feeScheduleId &&
      security?.fees?.discount === advisoryFeeTableData[0]?.fees?.discount
    );
  });
};

const isEqualSecurityRedemptionCharge = securitiesWithFrontLoad => {
  if (securitiesWithFrontLoad.length === 0) {
    return false;
  }
  const { redemptionCharge } = securitiesWithFrontLoad[0];
  if (typeof redemptionCharge === 'undefined') {
    return false;
  }
  return securitiesWithFrontLoad.every(security => security.redemptionCharge === redemptionCharge);
};

export const isEqualSecurityFrontLoad = securitiesWithFrontLoad => {
  if (securitiesWithFrontLoad.length === 0) {
    return false;
  }
  const { frontLoadFeeType, frontLoadAmount } = securitiesWithFrontLoad[0];
  if (typeof frontLoadFeeType === 'undefined') {
    return false;
  }
  if (
    frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT ||
    frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_PERCENT
  ) {
    return securitiesWithFrontLoad.every(
      security =>
        security.frontLoadFeeType === frontLoadFeeType &&
        security.frontLoadAmount === frontLoadAmount
    );
  }
  return securitiesWithFrontLoad.every(security => security.frontLoadFeeType === frontLoadFeeType);
};

export const getAnnuityData = vaSecuritiesPolices => {
  const annuityData = [];
  const policyIdSet = new Set();
  vaSecuritiesPolices.forEach(({ policyId, policyName }) => {
    if (!policyIdSet.has(policyId)) {
      policyIdSet.add(policyId);
      annuityData.push({
        policyId,
        policyName,
      });
    }
  });
  return annuityData;
};

const getAnnuityDataBySecurityId = (securityId, vaSecuritiesPolices, annuityOptionsData) => {
  const securityPolicy = vaSecuritiesPolices.find(i => i.secId === securityId);
  const { policyId } = securityPolicy ?? {};
  return annuityOptionsData.find(i => i.policyId === policyId);
};

export const updateVASecurityAnnuityData = (
  annuityOptionsData,
  vaSecuritiesPolices,
  securities
) => {
  if (annuityOptionsData.length > 0) {
    return securities.map(security => {
      const annuity = getAnnuityDataBySecurityId(
        security.securityId,
        vaSecuritiesPolices,
        annuityOptionsData
      );
      if (!annuity) {
        return security;
      }
      return {
        ...security,
        annuity,
      };
    });
  }
  return securities;
};

export const getAnnuityOptionsData = (securityTableData, vaSecuritiesPolices) => {
  const policySettingsMap = new Map();
  securityTableData
    .filter(security => security.securityType === 'VA')
    .forEach(security => {
      const { annuity } = security ?? {};
      if (annuity && annuity?.policyId) {
        policySettingsMap.set(annuity.policyId, annuity);
      }
    });
  return getAnnuityData(vaSecuritiesPolices).map(({ policyId, policyName }) => {
    return {
      policyId,
      policyName,
      ...policySettingsMap.get(policyId),
    };
  });
};

const getAssetBasedFeeConfigTypes = ({
  allowAssetBasedFeePercent = true,
  allowAssetBasedMaxFee = false,
  allowAssetBasedFeeSchedule = false,
}) => {
  let feeScheduleLabelType = 0; // Max Fee/Fee Schedule: 0:OFF, 1: Max Fee, 2:Fee Schedule, 3: Max Fee and Fee Schedule.
  if (allowAssetBasedMaxFee && !allowAssetBasedFeeSchedule) {
    feeScheduleLabelType = 1;
  } else if (!allowAssetBasedMaxFee && allowAssetBasedFeeSchedule) {
    feeScheduleLabelType = 2;
  } else if (allowAssetBasedMaxFee && allowAssetBasedFeeSchedule) {
    feeScheduleLabelType = 3;
  }
  const allowMaxFeeOrFeeSchedule = feeScheduleLabelType !== 0;
  // Wiki: https://mswiki.morningstar.com/pages/viewpage.action?spaceKey=FINPLAN&title=3.0+annual+fee%3A+fee+types
  // Config A: Only Flat Percent
  let assetBasedFeeConfigType = ONLY_FLAT_PERCENT;
  if (!allowAssetBasedFeePercent && allowMaxFeeOrFeeSchedule) {
    // Config B: Only MaxOrFeeSchedule
    assetBasedFeeConfigType = ONLY_FEE_CHOICE;
  } else if (allowAssetBasedFeePercent && allowMaxFeeOrFeeSchedule) {
    // Config C: Flat And MaxOrFeeSchedule
    assetBasedFeeConfigType = EVERYTHING_ON;
  } else if (!allowAssetBasedFeePercent && !allowMaxFeeOrFeeSchedule) {
    // Config D: Everythin Off
    assetBasedFeeConfigType = EVERYTHING_OFF;
  }
  return {
    feeScheduleLabelType,
    assetBasedFeeConfigType,
  };
};

const checkAdminSavedMaxOrFeeSchedule = (
  feeSchedule,
  allowAssetBasedFeeSchedule,
  allowAssetBasedMaxFee
) => {
  return (
    feeSchedule.isFirmSaved &&
    ((allowAssetBasedFeeSchedule && !isMaxFee(feeSchedule)) ||
      (allowAssetBasedMaxFee && isMaxFee(feeSchedule)))
  );
};

const checkFlatPercentVisibility = (assetBasedFeeConfig, userLevel, allFeeScheduleList) => {
  const {
    allowAssetBasedFeePercent,
    allowAssetBasedFeeSchedule,
    allowAssetBasedMaxFee,
    maxFeeOrFeeScheduleSource,
  } = assetBasedFeeConfig;
  const isNormalUser = userLevel === USER_LEVEL.NORMAL;
  const onlyMaxOrFeeScheduleAllowed =
    !allowAssetBasedFeePercent && (allowAssetBasedFeeSchedule || allowAssetBasedMaxFee);
  const onlyAdminCreatedAllowed =
    maxFeeOrFeeScheduleSource.includes(SOURCE.ADMIN_CREATED) &&
    !maxFeeOrFeeScheduleSource.includes(SOURCE.NORMAL_USER_CREATED);
  const needCheckAdminCreatedFeeSchedules =
    isNormalUser && onlyMaxOrFeeScheduleAllowed && onlyAdminCreatedAllowed;
  if (needCheckAdminCreatedFeeSchedules) {
    const adminCreatedFeeSchedules = allFeeScheduleList.filter(feeSchedule =>
      checkAdminSavedMaxOrFeeSchedule(
        feeSchedule,
        allowAssetBasedFeeSchedule,
        allowAssetBasedMaxFee
      )
    );
    return adminCreatedFeeSchedules.length === 0;
  }
  return allowAssetBasedFeePercent;
};

export const getAssetBasedFeeConfig = (
  assetBasedFeeConfig,
  settingsUserLevel,
  allFeeScheduleList
) => {
  const {
    allowAssetBasedFeePercent = true,
    allowAssetBasedMaxFee = false,
    allowAssetBasedFeeSchedule = false,
    maxFeeOrFeeScheduleSource = [0],
    userLevel = 0,
  } = assetBasedFeeConfig;
  const { assetBasedFeeConfigType, feeScheduleLabelType } = getAssetBasedFeeConfigTypes({
    allowAssetBasedFeePercent: checkFlatPercentVisibility(
      assetBasedFeeConfig,
      settingsUserLevel,
      allFeeScheduleList
    ),
    allowAssetBasedMaxFee,
    allowAssetBasedFeeSchedule,
  });

  return {
    ...assetBasedFeeConfig,
    allowAssetBasedFeePercent,
    allowAssetBasedMaxFee,
    allowAssetBasedFeeSchedule,
    maxFeeOrFeeScheduleSource,
    userLevel,
    assetBasedFeeConfigType,
    feeScheduleLabelType,
  };
};

export const getComponentProps = (isCollapsibleMode, expanded) => {
  if (isCollapsibleMode) {
    return {
      size: 7,
      collapsible: true,
      bold: true,
      border: 'secondary',
      expanded,
    };
  }
  return undefined;
};

export const getAdvisoryFeeTableMwcConfig = enableThreeReturns => {
  return {
    components: {
      AnnualFeeViewer: {
        settings: {
          enableThreeReturns,
        },
      },
    },
  };
};

export const applyRoaLoiAmountToSecurities = (event, securityTableData) => {
  const { roa, loi } = event;
  return securityTableData.map(security => {
    if (
      security.frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.STANDARD &&
      security.fundFamilyId !== undefined
    ) {
      let newSecurity = { ...security };
      const roaFundFamiliy = roa.fundFamilies.find(
        item => security.fundFamilyId === item.fundFamilyId
      );
      const loiFundFamiliy = loi.fundFamilies.find(
        item => security.fundFamilyId === item.fundFamilyId
      );
      newSecurity = {
        ...newSecurity,
        roaAmount: roaFundFamiliy?.fundFamilyAmount,
        loiAmount: loiFundFamiliy?.fundFamilyAmount,
      };
      return newSecurity;
    }
    return security;
  });
};

export const removeRoaLoiAmountFromSecurities = securityTableData => {
  return securityTableData.map(security => {
    const { roaAmount, loiAmount, ...securityInfo } = security;
    if (
      security.frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.STANDARD &&
      security.fundFamilyId !== undefined
    ) {
      return securityInfo;
    }
    return security;
  });
};

export const getUpdatedPortLevelIsVariousFlags = advisoryFeeTableData => {
  const data = advisoryFeeTableData;
  return {
    isVariousAnnualFeeData: !isEqualAnnualFeeData(data),
    isVariousFeeFrequency: !isEqualAnnualFrequency(data),
    isVariousAnnualFeeType: !isEqualAnnualFeeType(data),
    isVariousFeeScheduleId: !isEqualFeeScheduleId(data),
    isVariousFlatAnnualFeeAmount: !isEqualFlatAnnualFeeAmount(data),
    isVariousAnnualMaxFee: !isEqualAnnualMaxFeeAmount(data),
    isVariousDiscount: !isEqualDiscount(data),
  };
};

const getUpdatedPortLevelAdvisoryData = advisoryFeeTableData => {
  const data = advisoryFeeTableData;
  const firstSecFees = data[0].fees;
  return {
    annualMaxFee: isEqualAnnualMaxFeeAmount(data) ? firstSecFees.annualMaxFee : null,
    discount: isEqualDiscount(data) ? firstSecFees.discount : null,
    annualFeeAmount: isEqualFlatAnnualFeeAmount(data) ? firstSecFees.annualFeeAmount : null,
    annualFeeChargeFrequency: isEqualAnnualFrequency(data)
      ? firstSecFees.annualFeeChargeFrequency
      : null,
    annualFeeType: isEqualAnnualFeeType(data) ? firstSecFees.annualFeeType : VARIES,
    feeScheduleId: isEqualFeeScheduleId(data) ? firstSecFees.feeScheduleId : null,
  };
};

const getUpdatedPortLevelAdvisoryFeeSchedule = (
  advisoryFeeSetting,
  advisoryFeeTableData,
  allFeeScheduleList
) => {
  const sameAdvisoryFeeSchedule =
    isEqualFeeScheduleId(advisoryFeeTableData) && Array.isArray(allFeeScheduleList);
  const advisoryFeeSchedule = allFeeScheduleList.find(
    feeSchedule => feeSchedule.feeScheduleId === advisoryFeeSetting.feeScheduleId
  );
  if (sameAdvisoryFeeSchedule && advisoryFeeSchedule) {
    return { advisoryFeeSchedule };
  }
  return { advisoryFeeSchedule: undefined };
};

export const getUpdatedAdvisoryFeeSetting = (
  advisoryFeeSetting,
  advisoryFeeTableData,
  allFeeScheduleList
) => {
  return {
    ...advisoryFeeSetting,
    ...getUpdatedPortLevelIsVariousFlags(advisoryFeeTableData),
    ...getUpdatedPortLevelAdvisoryData(advisoryFeeTableData),
    ...getUpdatedPortLevelAdvisoryFeeSchedule(
      advisoryFeeSetting,
      advisoryFeeTableData,
      allFeeScheduleList
    ),
  };
};

export const updateSMASecuritiesManagerFee = (smaSecuritiesBaseInfo, securities) => {
  if (smaSecuritiesBaseInfo.length > 0) {
    return securities.map(security => {
      const smaSecurity = smaSecuritiesBaseInfo.find(
        item => item.securityId === security.securityId
      );
      if (smaSecurity) {
        return {
          ...security,
          managerFee: smaSecurity.managerFee,
        };
      }
      return security;
    });
  }
  return securities;
};

export const getSMASecuritiesBaseInfo = async (securities, languageId, environment) => {
  const smaSecIds = securities
    .filter(security => isSeparateAccount(security.securityType))
    .map(security => security.securityId);
  if (smaSecIds.length === 0) {
    return [];
  }
  return getSMASecuritiesBaseInfoFromApi(smaSecIds, languageId, environment);
};

export const getUpdatedPlanFeeSetting = planFeeData => {
  const { includePlanFee, planFeeType, planFee } = planFeeData;
  const result = {};
  result.includePlanFee = includePlanFee;
  if (planFeeType === ESTIMATED_PERCENT) {
    result.estimatedPlanFee = planFee;
  } else if (planFeeType === BENCHMARK_PERCENT) {
    result.benchmarkPlanFee = planFee;
  } else {
    result.customPlanFee = planFee;
  }
  return result;
};

export const applyFrontLoadForSecurities = (frontLoadFeeData, securityTableData) => {
  const { frontLoadFeeType, frontLoadAmount } = frontLoadFeeData;
  return securityTableData.map(security => {
    const newSecurityData = {
      ...security,
      frontLoadFeeType,
    };
    if (
      frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT ||
      frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_PERCENT
    ) {
      newSecurityData.frontLoadAmount = frontLoadAmount;
    }
    return newSecurityData;
  });
};

export const getTransactionFeeSettingWithDataOmitted = (
  transactionFeeSetting,
  securitiesWithFrontLoad
) => {
  const oldTransactionFeeSetting = omit(transactionFeeSetting, [
    'frontLoadAmountType',
    'frontLoadAmount',
  ]);
  return {
    ...oldTransactionFeeSetting,
    redemptionCharge: isEqualSecurityRedemptionCharge(securitiesWithFrontLoad)
      ? securitiesWithFrontLoad[0].redemptionCharge
      : 2,
  };
};

export const getUpdatedTransactionFeeSettingWhenEqualFrontLoad = securitiesWithFrontLoad => {
  if (!isEqualSecurityFrontLoad(securitiesWithFrontLoad)) {
    return { frontLoadType: VAH };
  }
  const result = {};
  const frontLoadFeeType = securitiesWithFrontLoad[0].frontLoadFeeType;
  const { frontLoadType, frontLoadAmountType } = frontLoadTypeDataMapping(frontLoadFeeType);
  result.frontLoadType = frontLoadType;
  if (typeof frontLoadAmountType !== 'undefined') {
    result.frontLoadAmountType = frontLoadAmountType;
    result.frontLoadAmount = securitiesWithFrontLoad[0].frontLoadAmount;
  }
  return result;
};

export const getDefaultTransactionFeeSettingWhenNotShow = (
  showTransactionFee,
  transactionFeeSetting,
  defaultVals
) => {
  if (!showTransactionFee && transactionFeeSetting.includeTransactionFee) {
    return {
      frontLoadType: defaultVals.frontLoadType,
      frontLoadAmount: defaultVals.frontLoadAmount,
      frontLoadAmountType: defaultVals.frontLoadAmountType,
      redemptionCharge: defaultVals.redemptionCharge,
      chargeOnRebalancing: defaultVals.chargeOnRebalancing,
    };
  }
  return {};
};

const generateTransactionFeeWithDefaultVals = (
  fees,
  defaultVals,
  showRoaLoi,
  showLoiAsFrontLoad
) => {
  let { frontLoadType = defaultVals.frontLoadType } = fees ?? {};
  const {
    frontLoadAmount = defaultVals.frontLoadAmount,
    frontLoadAmountType = defaultVals.frontLoadAmountType,
    redemptionCharge = defaultVals.redemptionCharge,
    chargeOnRebalancing = defaultVals.chargeOnRebalancing,
  } = fees ?? {};
  if (showRoaLoi || !showLoiAsFrontLoad) {
    frontLoadType = frontLoadType === LOI ? STANDARD : frontLoadType;
  }
  return {
    frontLoadType,
    frontLoadAmount,
    frontLoadAmountType,
    redemptionCharge,
    chargeOnRebalancing,
  };
};

const assembleFeesSecitonAccordingToConfigs = (data, showLoiAsFrontLoad, showRoaLoi) => {
  const {
    frontLoadType,
    frontLoadAmount,
    frontLoadAmountType,
    redemptionCharge,
    chargeOnRebalancing,
  } = data;
  let fees = {
    frontLoadType,
    redemptionCharge,
    chargeOnRebalancing,
  };
  if (showLoiAsFrontLoad && !showRoaLoi && frontLoadType === LOI) {
    fees = {
      ...fees,
      loi: true,
    };
  } else if (frontLoadType === CUSTOM) {
    const amount =
      frontLoadAmountType === PERCENT && !isPercentageInRange(frontLoadAmount, 0, 100)
        ? 0
        : frontLoadAmount;
    fees = {
      ...fees,
      frontLoadAmount: amount,
      frontLoadAmountType,
    };
  }
  return fees;
};

export const updateSecuritiesIfTransactionFeeAbsent = (
  transactionFeeSetting,
  securities,
  defaultVals,
  showRoaLoi,
  showLoiAsFrontLoad
) => {
  const { includeTransactionFee } = transactionFeeSetting;
  return securities.map(security => {
    let { fees } = security;
    if (includeTransactionFee) {
      const modifiedTransactionFeeSettingVals = generateTransactionFeeWithDefaultVals(
        security.fees,
        defaultVals,
        showRoaLoi,
        showLoiAsFrontLoad
      );
      fees = assembleFeesSecitonAccordingToConfigs(
        modifiedTransactionFeeSettingVals,
        showLoiAsFrontLoad,
        showRoaLoi
      );
    }
    return {
      ...security,
      fees,
    };
  });
};

export const transformSecuritiesTableData = securities => {
  return securities.map(security => {
    const { fees, ...securityInfo } = security;
    if (typeof fees !== 'undefined' && typeof fees.frontLoadType !== 'undefined') {
      const { frontLoadType, frontLoadAmountType, frontLoadAmount, redemptionCharge } = fees;
      const redemptionChargeValue = redemptionCharge ? 1 : 0;
      let frontLoadFeeType = `${frontLoadType}`;
      const updatedSecurity = {
        ...securityInfo,
        redemptionCharge: redemptionChargeValue,
      };
      if (frontLoadType === CUSTOM) {
        if (frontLoadAmountType === AMOUNT) {
          frontLoadFeeType = FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT;
        } else {
          frontLoadFeeType = FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_PERCENT;
        }
        return {
          ...updatedSecurity,
          frontLoadFeeType,
          frontLoadAmount,
        };
      }
      return {
        ...updatedSecurity,
        frontLoadFeeType,
      };
    }
    return security;
  });
};

const getAdvisoryFeeVals = (
  includeAdvisoryFee,
  advisoryFeeTableData,
  security,
  allFeeScheduleList,
  defaultDiscount
) => {
  if (includeAdvisoryFee) {
    let securityWithAdvisoryFee = advisoryFeeTableData.find(
      item => item.securityId === security.securityId
    );
    securityWithAdvisoryFee = setAdvisoryFeeDataForSecurity(
      securityWithAdvisoryFee,
      securityWithAdvisoryFee?.fees?.feeScheduleId ?? '',
      allFeeScheduleList,
      defaultDiscount
    );
    return omit(securityWithAdvisoryFee.fees, [
      'frontLoadType',
      'frontLoadAmount',
      'frontLoadAmountType',
      'loi',
      'chargeOnRebalancing',
      'redemptionCharge',
    ]); // omit transaction fee data points
  }
  return {};
};

const getDefaultAdvisoryFeeValsWhenNotShowAdvisoryFee = (
  showAdvisoryFee,
  includeAdvisoryFee,
  defaultVals
) => {
  if (!showAdvisoryFee && includeAdvisoryFee) {
    return {
      annualFeeType: defaultVals.annualFeeType,
      annualFeeAmount: defaultVals.annualFeeAmount,
      annualFeeAmountType: defaultVals.annualFeeAmountType,
      feeScheduleId: defaultVals.feeScheduleId,
      annualFeeChargeFrequency: defaultVals.annualFeeChargeFrequency,
    };
  }
  return [];
};

const getTransactionFeeVals = (
  includeTransactionFee,
  frontLoadFeeType,
  redemptionCharge,
  showLoiAsFrontLoad,
  frontLoadAmount
) => {
  if (includeTransactionFee && typeof frontLoadFeeType !== 'undefined') {
    const { frontLoadType, frontLoadAmountType } = frontLoadTypeDataMapping(frontLoadFeeType);
    let result = {
      frontLoadType,
      redemptionCharge: redemptionCharge === 1,
    };
    if (showLoiAsFrontLoad) {
      result = {
        ...result,
        loi: frontLoadType === LOI,
      };
    }
    if (typeof frontLoadAmountType !== 'undefined') {
      result = {
        ...result,
        frontLoadAmountType,
        frontLoadAmount,
      };
    }
    return result;
  }
  return {};
};

const getDefaultTransactionFeeValsWhenNotShowTransactionFee = (
  showTransactionFee,
  includeTransactionFee,
  defaultVals
) => {
  if (!showTransactionFee && includeTransactionFee) {
    return {
      frontLoadType: defaultVals.frontLoadType,
      frontLoadAmount: defaultVals.frontLoadAmount,
      frontLoadAmountType: defaultVals.frontLoadAmountType,
      redemptionCharge: defaultVals.redemptionCharge,
    };
  }
  return {};
};

export const setFeesToSecuritiesByPortLevelSettingsAndConfigs = thisObj => {
  const {
    securities,
    advisoryFeeSetting,
    transactionFeeSetting,
    securityTableData,
    advisoryFeeTableData,
    showAdvisoryFee,
    showLoiAsFrontLoad,
    showTransactionFee,
    defaultVals,
    allFeeScheduleList,
    settings,
  } = thisObj;
  return securities.map(security => {
    const { includeAdvisoryFee } = advisoryFeeSetting;
    const { includeTransactionFee } = transactionFeeSetting;
    const securityWithTransactionFee = securityTableData.find(
      item => item.securityId === security.securityId
    );
    const { frontLoadAmount, redemptionCharge, ...securityInfo } = security;
    const { frontLoadFeeType } = securityWithTransactionFee ?? {};
    if (
      !includeAdvisoryFee &&
      (!includeTransactionFee || (includeTransactionFee && frontLoadFeeType === undefined))
    ) {
      return securityInfo;
    }
    const fees = {
      ...getAdvisoryFeeVals(
        includeAdvisoryFee,
        advisoryFeeTableData,
        security,
        allFeeScheduleList,
        settings?.preference.discount
      ),
      ...getDefaultAdvisoryFeeValsWhenNotShowAdvisoryFee(
        showAdvisoryFee,
        includeAdvisoryFee,
        defaultVals
      ),
      ...getTransactionFeeVals(
        includeTransactionFee,
        frontLoadFeeType,
        redemptionCharge,
        showLoiAsFrontLoad,
        frontLoadAmount
      ),
      ...getDefaultTransactionFeeValsWhenNotShowTransactionFee(
        showTransactionFee,
        includeTransactionFee,
        defaultVals
      ),
    };
    return {
      ...securityInfo,
      fees,
    };
  });
};

export const applyAffectedKeysToSecurities = (
  newAdvisoryFeeSetting,
  advisoryFeeTableData,
  allFeeScheduleList
) => {
  const {
    annualFeeAmount,
    annualFeeChargeFrequency,
    annualFeeType,
    feeScheduleId,
    annualMaxFee,
    discount,
  } = newAdvisoryFeeSetting;
  const advisoryFeeSchedule = getAdvisoryFeeSchedule(feeScheduleId, allFeeScheduleList);
  return advisoryFeeTableData.map(({ fees, ...security }) => {
    return {
      ...security,
      advisoryFeeSchedule,
      fees: {
        ...fees,
        annualFeeAmount: annualFeeAmount ?? fees.annualFeeAmount,
        annualFeeChargeFrequency: annualFeeChargeFrequency ?? fees.annualFeeChargeFrequency,
        annualFeeType,
        feeScheduleId,
        annualMaxFee,
        discount,
      },
    };
  });
};

export const isSecurityWithTradingCommission = sec => {
  return ['ST', 'FC', 'FE'].includes(sec.securityType);
};

const applyDefaultAdvisoryFee = (fees, assetBasedFeeConfigType, defaultVals) => {
  const newFees = omit(fees, [
    'annualFeeType',
    'annualFeeAmount',
    'annualFeeChargeFrequency',
    'feeScheduleId',
    'annualMaxFee',
    'discount',
  ]);
  if ([EVERYTHING_OFF, ONLY_FLAT_PERCENT].includes(assetBasedFeeConfigType)) {
    fees = {
      ...newFees,
      annualFeeType: FLAT,
      annualFeeAmount: defaultVals.annualFeeAmount,
      annualFeeChargeFrequency: defaultVals.annualFeeChargeFrequency,
    };
  } else if (
    assetBasedFeeConfigType === ONLY_FEE_CHOICE ||
    assetBasedFeeConfigType === EVERYTHING_ON
  ) {
    fees = {
      ...newFees,
      annualFeeType: MAX_OR_FEE_SCHEDULE,
      annualFeeAmount: defaultVals.annualFeeAmount,
      annualFeeChargeFrequency: defaultVals.annualFeeChargeFrequency,
      feeScheduleId: defaultVals.feeScheduleId,
      discount: defaultVals.discount,
    };
  }
  return fees;
};

const convertFeeDataAccordingToConfig = (fees, assetBasedFeeConfig, defaultVals) => {
  const { assetBasedFeeConfigType } = assetBasedFeeConfig;
  const { annualFeeType } = fees;
  if (
    annualFeeType === MAX_OR_FEE_SCHEDULE &&
    [EVERYTHING_OFF, ONLY_FLAT_PERCENT].includes(assetBasedFeeConfigType)
  ) {
    // When config is percent only but old saved data contains fee schedule.
    const newFees = omit(fees, ['feeScheduleId', 'annualMaxFee', 'discount']);
    fees = {
      ...newFees,
      annualFeeType: FLAT,
      annualFeeAmountType: defaultVals.annualFeeAmountType,
      annualFeeAmount: defaultVals.annualFeeAmount,
    };
  } else if (annualFeeType === FLAT && assetBasedFeeConfigType === ONLY_FEE_CHOICE) {
    // When config is feeChoice only but old saved data contains percent.
    const newFees = omit(fees, [
      'annualFeeAmountType',
      'annualFeeAmount',
      'annualMaxFee',
      'discount',
    ]);
    fees = {
      ...newFees,
      annualFeeType: MAX_OR_FEE_SCHEDULE,
      feeScheduleId: '',
    };
  }
  return fees;
};

export const processSecurityData = (
  securities,
  assetBasedFeeConfig,
  allFeeScheduleList,
  defaultVals
) => {
  const newSecurities = cloneDeep(securities);
  return newSecurities.map(security => {
    const { fees = {} } = security;
    const { annualFeeType } = fees;
    if (typeof annualFeeType !== 'undefined') {
      security.fees = convertFeeDataAccordingToConfig(fees, assetBasedFeeConfig, defaultVals);
      return setAdvisoryFeeDataForSecurity(
        security,
        security.fees.feeScheduleId,
        allFeeScheduleList,
        defaultVals.discount
      );
    }
    return {
      ...security,
      fees: applyDefaultAdvisoryFee(fees, assetBasedFeeConfig.assetBasedFeeConfigType, defaultVals),
    };
  });
};

const hasAssetBasedFee = security => {
  const containsMaxScheduleFee =
    security?.fees?.annualFeeType === 0 &&
    typeof security?.fees?.feeScheduleId !== 'undefined' &&
    security?.fees?.feeScheduleId !== '' &&
    !equal(security?.fees?.discount || 0, 100);
  const containsFlatFee =
    security?.fees?.annualFeeType === 1 &&
    typeof security?.fees?.annualFeeAmount !== 'undefined' &&
    security?.fees?.annualFeeAmount > 0;
  return containsMaxScheduleFee || containsFlatFee;
};

export const checkAssetBasedFee = thisObj => {
  const securityFeesData = setFeesToSecuritiesByPortLevelSettingsAndConfigs(thisObj);
  const { alertAssetBasedFeeRequired } = thisObj.settings.assetBasedFeeConfig;
  let requiredAssetBasedFeeMsg = '';
  let requiredAssetBasedFeeValid = true;
  if (alertAssetBasedFeeRequired === 1) {
    requiredAssetBasedFeeMsg = `${thisObj.translate('requireAssetBasedFee')} ${thisObj.translate(
      'requireAllHoldingsMsg'
    )}`;
    requiredAssetBasedFeeValid = securityFeesData.every(security => hasAssetBasedFee(security));
  } else if (alertAssetBasedFeeRequired === 2) {
    requiredAssetBasedFeeMsg = `${thisObj.translate('requireAssetBasedFee')} ${thisObj.translate(
      'requireAtLeastOneHoldingMsg'
    )}`;
    requiredAssetBasedFeeValid = securityFeesData.some(security => hasAssetBasedFee(security));
  }
  if (!requiredAssetBasedFeeValid) {
    thisObj.requireAssetBaseFee = {
      text: requiredAssetBasedFeeMsg,
    };
  } else {
    thisObj.requireAssetBaseFee = null;
  }
  return requiredAssetBasedFeeValid;
};
